// Identifier-first email field (ADR-015): Continue → Google when the domain
// is known-Google, otherwise Identity Platform email-link. Escape hatch
// always available. Shown when /api/health features.emailLink.enabled is true.
import { useState } from "react";
import { sendSignInLinkToEmail } from "firebase/auth";
import { isGoogleLoginDomain } from "@rrid/shared-types";
import { Button, Input } from "../components/ui.js";
import { api, ApiError } from "../lib/api.js";
import { EMAIL_FOR_SIGN_IN_KEY, getFirebaseAuth } from "../lib/firebaseAuth.js";
import { describeEmailLinkCompleteError, persistLoginError } from "../lib/loginError.js";
import { googleAuthorizeHref, readLoginHint } from "../lib/loginHint.js";

const SIGNIN_FROM = "noreply@mail.rrid-ucb.org";

export function EmailLinkSignIn({
  firebase,
  openAccess,
  googleEnabled,
  initialEmail,
  onClearLoginError,
  onLoginError,
}: {
  firebase: { apiKey: string; authDomain: string; projectId: string };
  openAccess: boolean;
  googleEnabled: boolean;
  initialEmail?: string;
  onClearLoginError?: () => void;
  onLoginError?: (message: string) => void;
}): JSX.Element {
  const remembered = readLoginHint();
  const [email, setEmail] = useState(initialEmail || remembered?.email || "");
  const [forceLink, setForceLink] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmed = email.trim();
  const routeToGoogle = googleEnabled && !forceLink && Boolean(trimmed) && isGoogleLoginDomain(trimmed);

  return (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!trimmed) return;
        setBusy(true);
        setError(null);
        onClearLoginError?.();
        setSentTo(null);
        try {
          try {
            await api.post("/auth/email-link/check", { email: trimmed });
          } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
              const described = describeEmailLinkCompleteError(err);
              persistLoginError(described.message);
              onLoginError?.(described.message);
              setError(described.message);
              return;
            }
            throw err;
          }
          if (googleEnabled && !forceLink && isGoogleLoginDomain(trimmed)) {
            window.location.assign(googleAuthorizeHref(trimmed));
            return;
          }
          const auth = getFirebaseAuth(firebase);
          await sendSignInLinkToEmail(auth, trimmed, {
            url: `${window.location.origin}/login/email-link`,
            handleCodeInApp: true,
          });
          window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
          setSentTo(trimmed);
        } catch (err) {
          const code =
            err && typeof err === "object" && "code" in err ? String((err as { code: unknown }).code) : "";
          const message =
            code === "auth/invalid-email"
              ? "Enter a valid email address."
              : "Could not send the sign-in email. Try again in a moment.";
          persistLoginError(message);
          onLoginError?.(message);
          setError(message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-600">Email</span>
        <Input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setForceLink(false);
          }}
          placeholder={openAccess ? "any email address" : "same email as SoftR"}
        />
      </label>
      <Button className="w-full" variant="secondary" type="submit" disabled={busy || !trimmed}>
        {busy ? (routeToGoogle ? "Continuing…" : "Sending…") : routeToGoogle ? "Continue" : "Email me a sign-in link"}
      </Button>
      {googleEnabled && routeToGoogle ? (
        <p className="text-center text-xs text-slate-500">
          This address can use Google.{" "}
          <button
            type="button"
            className="font-medium text-[#0b5cab] underline-offset-2 hover:underline"
            onClick={() => setForceLink(true)}
          >
            Use a sign-in email instead
          </button>
        </p>
      ) : null}
      {googleEnabled && forceLink ? (
        <p className="text-center text-xs text-slate-500">
          We will email a one-time link instead of opening Google.
        </p>
      ) : null}
      {sentTo ? (
        <div
          className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-3 text-sm text-amber-950"
          role="status"
        >
          <p className="text-base font-semibold">Check your inbox (and Spam if it is not there).</p>
          <p className="mt-1">
            We emailed a sign-in link to <span className="font-medium">{sentTo}</span>. A new sending
            domain can still land in spam the first time — if it does, mark it not spam.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>
              From: <code className="rounded bg-amber-100 px-1 text-xs">{SIGNIN_FROM}</code>
            </li>
            <li>
              Subject starts with <span className="font-medium">Sign in to RR\ID</span>
            </li>
            <li>
              Open it, then click <span className="font-medium">Report not spam</span> if Gmail filed
              it there.
            </li>
          </ul>
        </div>
      ) : null}
      {error ? (
        <div
          className="rounded-md border-2 border-red-300 bg-red-50 px-3 py-3 text-sm text-red-950"
          role="alert"
          aria-live="assertive"
        >
          <p className="font-semibold">{error}</p>
        </div>
      ) : null}
    </form>
  );
}
